<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { RouterLink } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisField from 'dashboard/components-next/LaisField.vue';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import ProfileConfirmBanner from '../components/ProfileConfirmBanner.vue';
import {
  clearCookiesOnLogout,
  parseAPIErrorResponse,
} from 'dashboard/store/utils/api';

const store = useStore();
const account = useAccount();
const currentUser = useMapGetter('getCurrentUser');
const currentRole = useMapGetter('getCurrentRole');
const globalConfig = useMapGetter('globalConfig/get');

const avatarFile = ref(null);
const avatarPreviewUrl = ref('');
const isSaving = ref(false);
const isDeletingAvatar = ref(false);
const nameInputRef = ref(null);

const focusProfileForm = () => {
  nameInputRef.value?.focus?.();
  nameInputRef.value?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
};

const form = reactive({
  name: '',
  displayName: '',
  email: '',
  phoneNumber: '',
});

const roleLabels = {
  administrator: 'Administrador',
  agent: 'Corretor',
  captador: 'Captador',
  operador: 'Operador (back-office)',
  custom_role: 'Acesso customizado',
};
const operatorRoleLabel = 'Operador (back-office)';
const noProfilePermissionMessage =
  'Você não possui permissão para modificar este campo.';
const emailAlreadyUsedMessage = 'Este email já está em uso:';
const emailInvalidMessage = 'Este endereço de Email não parece ser válido:';
const defaultProfileErrorMessage =
  'Não foi possível atualizar seus dados agora.';

const inputClass =
  'w-full rounded-lais border border-lais-border px-3 py-2.5 text-[14px] text-lais-text bg-white transition-all placeholder:text-[#94a3b8] focus:outline-none focus:border-lais-primary focus:ring-2 focus:ring-[#efe8fb] disabled:bg-[#f8fafc] disabled:text-[#94a3b8]';

const accountSummary = computed(() => account.currentAccount.value || {});
const currentAccountMembership = computed(() => {
  const accountId = Number(account.accountId.value);
  return (
    currentUser.value?.accounts?.find(
      membership => Number(membership.id) === accountId
    ) || {}
  );
});
const currentCustomRole = computed(
  () =>
    currentAccountMembership.value?.custom_role ||
    currentAccountMembership.value?.customRole ||
    {}
);
const normalizedRoleName = value =>
  value
    ?.toString()
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
const customRoleLabel = computed(() => {
  const roleName = currentCustomRole.value?.name?.toString().trim();
  if (!roleName) {
    return roleLabels.custom_role;
  }

  return normalizedRoleName(roleName).includes('operador')
    ? operatorRoleLabel
    : roleName;
});
const hasCustomRole = computed(
  () =>
    Boolean(currentAccountMembership.value?.custom_role_id) ||
    Boolean(currentAccountMembership.value?.customRoleId) ||
    Boolean(currentCustomRole.value?.name)
);
const roleLabel = computed(() => {
  if (hasCustomRole.value || currentRole.value === 'custom_role') {
    return customRoleLabel.value;
  }

  return roleLabels[currentRole.value] || 'Usuário autenticado';
});
const isAdministrator = computed(() => currentRole.value === 'administrator');
const profileUpdateDisabled = computed(() =>
  Boolean(globalConfig.value?.disableUserProfileUpdate)
);
const readOnlyFieldHelper = computed(() =>
  profileUpdateDisabled.value ? noProfilePermissionMessage : ''
);
const displayNameHelper = computed(() =>
  profileUpdateDisabled.value
    ? noProfilePermissionMessage
    : 'Opcional. Se preenchido, aparece em áreas compactas do app.'
);
const emailFieldHelper = computed(() =>
  profileUpdateDisabled.value
    ? noProfilePermissionMessage
    : 'Confirme seu e-mail antes de salvar alterações de acesso.'
);
const phoneFieldHelper = computed(() =>
  profileUpdateDisabled.value
    ? noProfilePermissionMessage
    : 'Esse número não é seu? Entre em contato com o suporte.'
);
const isEmailFormatValid = email => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
const errorToText = error => {
  const parsedError = parseAPIErrorResponse(error);
  if (Array.isArray(parsedError)) {
    return parsedError.join(' ');
  }
  if (parsedError && typeof parsedError === 'object') {
    return JSON.stringify(parsedError);
  }
  return parsedError?.toString?.() || '';
};
const profileErrorMessage = (error, email) => {
  const message = errorToText(error);
  const normalizedMessage = message.toLowerCase();

  if (
    normalizedMessage.includes('email') &&
    (normalizedMessage.includes('already') ||
      normalizedMessage.includes('taken') ||
      normalizedMessage.includes('uso') ||
      normalizedMessage.includes('cadastrado'))
  ) {
    return `${emailAlreadyUsedMessage} ${email}`;
  }

  return message || defaultProfileErrorMessage;
};

const quickLinks = computed(() => {
  const links = [
    {
      label: 'Preferências de notificações',
      helper: 'Ajuste alertas por e-mail e push do seu próprio usuário.',
      to: account.accountScopedRoute(
        'notifications_index',
        {},
        { tab: 'preferencias' }
      ),
    },
    {
      label: 'Segurança avançada',
      helper:
        'Abra senha, MFA, assinatura e demais ajustes avançados do perfil.',
      to: account.accountScopedRoute('profile_settings_index'),
    },
    {
      label: 'Privacidade',
      helper:
        'Veja como a Imudei usa seus dados e quais controles estão disponíveis.',
      to: account.accountScopedRoute('privacidade_index'),
    },
    {
      label: 'Termos de uso',
      helper: 'Consulte as regras operacionais e os links legais da conta.',
      to: account.accountScopedRoute('termos_index'),
    },
  ];

  if (isAdministrator.value) {
    links.push({
      label: 'Deletar conta',
      helper: 'Agende ou acompanhe a exclusão definitiva da conta.',
      to: account.accountScopedRoute('deletar_conta_index'),
    });
  }

  return links;
});

watch(
  currentUser,
  user => {
    form.name = user?.name || '';
    form.displayName = user?.display_name || user?.displayName || '';
    form.email = user?.email || '';
    form.phoneNumber =
      user?.custom_attributes?.phone_number || user?.phone_number || '';

    if (!avatarFile.value) {
      avatarPreviewUrl.value = user?.avatar_url || '';
    }
  },
  { immediate: true, deep: true }
);

const onAvatarUpload = ({ file, url }) => {
  avatarFile.value = file;
  avatarPreviewUrl.value = url;
};

const removeAvatar = async () => {
  isDeletingAvatar.value = true;
  try {
    await store.dispatch('deleteAvatar');
    avatarFile.value = null;
    avatarPreviewUrl.value = '';
    useAlert('Foto removida com sucesso.');
  } catch {
    useAlert('Não foi possível remover a foto do seu perfil.');
  } finally {
    isDeletingAvatar.value = false;
  }
};

const saveProfile = async () => {
  if (profileUpdateDisabled.value) {
    useAlert(noProfilePermissionMessage);
    return;
  }

  if (!form.name.trim()) {
    useAlert('Preencha seu nome antes de salvar.');
    return;
  }

  if (!form.email.trim()) {
    useAlert('Preencha seu e-mail antes de salvar.');
    return;
  }

  const nextEmail = form.email.trim();

  if (!isEmailFormatValid(nextEmail)) {
    useAlert(`${emailInvalidMessage} ${nextEmail}`);
    return;
  }

  const emailChanged = currentUser.value?.email !== nextEmail;

  isSaving.value = true;
  try {
    await store.dispatch('updateProfile', {
      name: form.name.trim(),
      displayName: form.displayName.trim(),
      email: nextEmail,
      phone_number: form.phoneNumber.trim(),
      avatar: avatarFile.value || undefined,
    });

    avatarFile.value = null;
    useAlert(
      emailChanged
        ? 'Seus dados foram salvos. Vamos encerrar a sessão para confirmar o novo e-mail.'
        : 'Seus dados foram atualizados com sucesso.'
    );

    if (emailChanged) {
      window.setTimeout(() => clearCookiesOnLogout(), 1200);
    }
  } catch (error) {
    useAlert(profileErrorMessage(error, nextEmail));
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <section class="flex h-full flex-col overflow-hidden bg-[#fcfaf6]">
    <LaisHeader
      title="Meus dados"
      helper="Atualize as informações da sua conta e acesse rapidamente os controles pessoais da Imudei."
    >
      <template #actions>
        <LaisPrimaryButton
          icon="i-lucide-save"
          :is-loading="isSaving"
          @click="saveProfile"
        >
          Salvar dados
        </LaisPrimaryButton>
      </template>
    </LaisHeader>

    <div class="flex-1 overflow-auto px-4 py-4 sm:px-6 lg:px-10 lg:py-6">
      <ProfileConfirmBanner class="mb-5" @confirm="focusProfileForm" />
      <div
        class="grid gap-5 xl:grid-cols-[minmax(0,1.4fr)_minmax(320px,0.9fr)]"
      >
        <div class="grid gap-5">
          <LaisCard title="Identidade do usuário">
            <div class="flex flex-col gap-5 lg:flex-row lg:items-start">
              <div class="flex items-center gap-4">
                <Avatar
                  :src="avatarPreviewUrl"
                  :name="form.name || 'Imudei'"
                  :size="88"
                  allow-upload
                  rounded-full
                  @upload="onAvatarUpload"
                  @delete="removeAvatar"
                />
                <div class="space-y-1">
                  <p class="text-[15px] font-semibold text-lais-text">
                    {{ form.name || 'Usuário Imudei' }}
                  </p>
                  <p class="text-[13px] text-lais-text-muted">
                    {{ roleLabel }} na conta
                    <strong class="text-lais-text">{{
                      accountSummary.name || 'Imudei'
                    }}</strong>
                  </p>
                  <p class="text-[12px] text-lais-text-muted">
                    Troque a foto pelo avatar no canto superior ou envie uma
                    nova imagem diretamente aqui.
                  </p>
                </div>
              </div>

              <div
                class="rounded-lais border border-dashed border-lais-border bg-white/70 px-4 py-3 text-[12px] text-lais-text-muted"
              >
                <p class="font-semibold text-lais-text">Resumo rápido</p>
                <p class="mt-1">
                  Conta ativa: {{ accountSummary.name || 'Imudei' }}
                </p>
                <p>ID da conta: {{ accountSummary.id || '—' }}</p>
                <p>
                  Nível de permissão:
                  <span class="font-semibold text-lais-text">{{
                    roleLabel
                  }}</span>
                </p>
                <p v-if="isDeletingAvatar" class="mt-2 text-lais-link">
                  Removendo foto...
                </p>
              </div>
            </div>
          </LaisCard>

          <LaisCard title="Dados cadastrais">
            <div class="grid gap-4 md:grid-cols-2">
              <LaisField label="Nome" :helper="readOnlyFieldHelper">
                <input
                  ref="nameInputRef"
                  v-model="form.name"
                  type="text"
                  :disabled="profileUpdateDisabled"
                  :class="inputClass"
                  placeholder="Nome"
                />
              </LaisField>

              <LaisField label="Nome de exibição" :helper="displayNameHelper">
                <input
                  v-model="form.displayName"
                  type="text"
                  :disabled="profileUpdateDisabled"
                  :class="inputClass"
                  placeholder="Ex.: Maria | Equipe Comercial"
                />
              </LaisField>

              <LaisField label="E-mail" required :helper="emailFieldHelper">
                <input
                  v-model="form.email"
                  type="email"
                  :disabled="profileUpdateDisabled"
                  :class="inputClass"
                  placeholder="Preencha com o seu e-mail"
                />
              </LaisField>

              <LaisField label="Telefone" :helper="phoneFieldHelper">
                <input
                  v-model="form.phoneNumber"
                  type="tel"
                  :disabled="profileUpdateDisabled"
                  :class="inputClass"
                  placeholder="(11) 00000-0000"
                />
              </LaisField>
            </div>
          </LaisCard>
        </div>

        <div class="grid gap-5">
          <LaisCard title="Atalhos pessoais">
            <div class="grid gap-3">
              <RouterLink
                v-for="link in quickLinks"
                :key="link.label"
                :to="link.to"
                class="rounded-lais border border-lais-border bg-white px-4 py-3 transition-all hover:-translate-y-0.5 hover:border-lais-primary hover:shadow-lais-soft"
              >
                <div class="flex items-center justify-between gap-3">
                  <div>
                    <p class="text-[14px] font-semibold text-lais-text">
                      {{ link.label }}
                    </p>
                    <p class="mt-1 text-[12px] leading-5 text-lais-text-muted">
                      {{ link.helper }}
                    </p>
                  </div>
                  <span
                    class="i-lucide-arrow-right size-4 shrink-0 text-lais-link"
                  />
                </div>
              </RouterLink>
            </div>
          </LaisCard>

          <LaisCard title="Sessão e segurança">
            <div class="space-y-3 text-[13px] leading-6 text-lais-text-muted">
              <p>
                Use a Imudei como superfície rápida do dia a dia e deixe os
                controles avançados, como MFA, senha, assinatura e integrações,
                concentrados na área de perfil da Imudei.
              </p>
              <div class="flex flex-wrap gap-2">
                <RouterLink
                  :to="account.accountScopedRoute('profile_settings_index')"
                  class="inline-flex items-center gap-2 rounded-lais border border-lais-border bg-white px-3 py-2 text-[13px] font-semibold text-lais-text transition-all hover:border-lais-primary hover:text-lais-link"
                >
                  <span class="i-lucide-shield-check size-4" />
                  Abrir segurança avançada
                </RouterLink>
                <RouterLink
                  :to="account.accountScopedRoute('logout_index')"
                  class="inline-flex items-center gap-2 rounded-lais border border-[#f1c9cf] bg-white px-3 py-2 text-[13px] font-semibold text-[#b42318] transition-all hover:bg-[#fff5f6]"
                >
                  <span class="i-lucide-log-out size-4" />
                  Sair da conta
                </RouterLink>
                <RouterLink
                  v-if="isAdministrator"
                  :to="account.accountScopedRoute('deletar_conta_index')"
                  class="inline-flex items-center gap-2 rounded-lais border border-[#f1c9cf] bg-white px-3 py-2 text-[13px] font-semibold text-[#b42318] transition-all hover:bg-[#fff5f6]"
                >
                  <span class="i-lucide-trash-2 size-4" />
                  Deletar conta
                </RouterLink>
              </div>
            </div>
          </LaisCard>
        </div>
      </div>
    </div>
  </section>
</template>
